/* global Handlebars */
const queryString = require("query-string");

// One day in milliseconds.
const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;

// Milliseconds to wait for most ajax to timeout.
const AJAX_TIMEOUT = 10000;

// Cookie duration for marketing cookies
const COOKIE_DURATION = 10 * 365 * ONE_DAY_IN_MS;

//
// Handlebars utilities
//

const tplCompileCache = {};

function compileTemplate(scriptTagQuerySelector) {
	if (tplCompileCache[scriptTagQuerySelector]) {
		return tplCompileCache[scriptTagQuerySelector];
	}
	let ret = "";

	const templateEl = document.querySelector(scriptTagQuerySelector);
	if (templateEl) {
		const templateText = templateEl.innerText || "";
		ret = Handlebars.compile(templateText);
	}

	tplCompileCache[scriptTagQuerySelector] = ret;
	return ret;
}

//
// General utilities
//

/**
 * Clamps a number between the specified minimum and maximum.
 *
 * @param {number} num
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
const clamp = (num, min, max) => Math.max(min, Math.min(max, num));

const getCookie = function(cookieName) {
	const reg = new RegExp(
		"(?:(?:^|.*;)\\s*" + cookieName + "\\s*\\=\\s*([^;]*).*$)|^.*$"
	);
	return decodeURIComponent(document.cookie.replace(reg, "$1")) || null;
};

const setCookie = function(name, value, duration = ONE_DAY_IN_MS) {
	let cookieVal = name + "=" + value + "; path=/;";
	let expiration = null;
	if (duration !== "session") {
		expiration = Date.now() + duration;
		const expDate = new Date(expiration).toUTCString();
		cookieVal += " expires=" + expDate + ";";
	}

	if (process.env.NODE_ENV === "production") {
		cookieVal += "secure;";
	}

	document.cookie = cookieVal;
	return expiration;
};

const clearCookie = function(name) {
	document.cookie = name + "=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;";
};

const hasPermKey = function(user, key) {
	const userPerms = user.perms || {};
	return !!userPerms[key];
};

const hasPermRole = function(user, role) {
	if (user && user.roles && user.roles.length) {
		return user.roles.indexOf(role) > -1;
	}
	return false;
};

// Copied from shared/StringUtil.js
const isValidEmail = function(email) {
	return email && /^[A-Z0-9._%+\-]+@([A-Z0-9\-]+\.)+[A-Z]{2,}$/i.test(email);
};

const isShallowEqual = function(obj1, obj2) {
	if (!obj1 || !obj2) return obj1 === obj2;
	if (typeof obj1 === "object") {
		const ok = Object.keys(obj1).every(k => obj1[k] === obj2[k]);
		if (!ok) return false;
	}
	if (typeof obj2 === "object") {
		const ok = Object.keys(obj2).every(k => obj1[k] === obj2[k]);
		if (!ok) return false;
	}
	return true;
};

const uniqBy = function(arr, f) {
	const ret = [];
	const seenValues = new Set();
	arr.forEach((item, index) => {
		const value = f(item, index, arr);
		if (!seenValues.has(value)) {
			seenValues.add(value);
			ret.push(item);
		}
	});
	return ret;
};

//
// General DOM utilities
//

function $find(el, selector) {
	return Array.from(el.querySelectorAll(selector));
}

function $each(el, selector, f) {
	const els = $find(el, selector);
	els.forEach(f);
}

function removeEl(el) {
	if (el) el.parentNode.removeChild(el);
}

function toggleClass(el, className, active) {
	el.classList.toggle(className, active);
}

function toggleAttribute(el, attributeName, value) {
	if (value) {
		el.setAttribute(attributeName, value);
	} else {
		el.removeAttribute(attributeName);
	}
}

function hasClass(el, className) {
	const classString = el.className || "";
	const classes = classString.split(" ");
	let part = 0;
	while (part < classes.length) {
		if (classes[part].indexOf(className) > -1) return true;
		part++;
	}
	return false;
}

function onClickOrEnter(el, func) {
	el.addEventListener("click", func);
	onEnter(el, func);
}

function onEnter(el, func) {
	el.addEventListener("keyup", evt => {
		if (evt.key === "Enter" || evt.code === "Enter" || evt.which === 13)
			func(evt);
	});
}

// Returns the value of the radio button that's selected within a radio group
function getRadioSelectedValue(groupName) {
	let selected;
	$each(document, `input[name=${groupName}]`, r => {
		if (r.checked) selected = r.value;
	});
	return selected;
}

function getSelectionValue(selector) {
	const elem = document.querySelector(selector);
	return elem.value;
}

// Returns the value of the checkboxes that's selected within a checkbox group
function getCheckboxSelectedValues(groupName, space) {
	const selected = [];
	$each(space || document, `input[name=${groupName}]`, r => {
		if (r.checked) selected.push(r.value);
	});
	return selected;
}

const setInnerHtmlIfDiff = function(el, text) {
	if (el.innerHTML !== text) el.innerHTML = text;
};

// Based on ScrollUtil's scrollTo
function scrollToEl(el, shortTopBar) {
	const rect = el && el.getBoundingClientRect();
	if (!rect) return;
	const nudge = shortTopBar ? 60 : 120; // Top bar is about 113 pixels.
	const globalScrollTop =
		window.pageYOffset ||
		document.documentElement.scrollTop ||
		document.body.scrollTop ||
		0;
	const finalPos = Math.max(rect.top + globalScrollTop - nudge, 0);
	document.documentElement.scrollTop = document.body.scrollTop = finalPos;
}

const isFocusable = function(element) {
	if (
		element.tabIndex > 0 ||
		(element.tabIndex === 0 && element.getAttribute("tabIndex") !== null)
	) {
		return true;
	}
	if (element.disabled) {
		return false;
	}
	switch (element.nodeName) {
		case "A":
			return !!element.href && element.rel !== "ignore";
		case "INPUT":
			return element.type !== "hidden" && element.type !== "file";
		case "BUTTON":
		case "SELECT":
		case "TEXTAREA":
			return true;
		default:
			return false;
	}
};

const getFocusableElements = function(parentEl) {
	// recursively go through element and children to find all focusable elements
	let focusableElements = [];
	if (isFocusable(parentEl)) focusableElements.push(parentEl);
	for (let i = 0; i < parentEl.childNodes.length; i++) {
		const child = parentEl.childNodes[i];
		focusableElements = focusableElements.concat(getFocusableElements(child));
	}
	return focusableElements;
};

let notImplementedEl;
let notImplementedTimeout;

// eslint-disable-next-line no-unused-vars
function notImplemented() {
	if (!notImplementedEl) {
		const outer = document.createElement("div");
		outer.style.position = "fixed";
		outer.style.left = "0";
		outer.style.top = "0";
		outer.style.right = "0";
		outer.style.bottom = "0";
		outer.style.display = "flex";
		outer.style.alignItems = "center";
		outer.style.justifyContent = "center";
		outer.style.zIndex = "1000000000";
		const inner = document.createElement("div");
		inner.innerHTML = "NOPE";
		inner.style.width = "700px";
		inner.style.height = "250px";
		inner.style.backgroundColor = "red";
		inner.style.color = "white";
		inner.style.fontSize = "220px";
		inner.style.lineHeight = "220px";
		inner.style.textAlign = "center";
		inner.style.transform = "rotate(-20deg)";
		inner.style.transformOrigin = "center";
		outer.appendChild(inner);

		notImplementedEl = outer;
	}

	if (notImplementedTimeout) {
		clearTimeout(notImplementedTimeout);
	} else {
		document.body.appendChild(notImplementedEl);
	}
	notImplementedTimeout = setTimeout(function() {
		document.body.removeChild(notImplementedEl);
		notImplementedTimeout = 0;
	}, 800);
}

const getItemsWithSubmenus = function() {
	return document.querySelectorAll(".has-submenu");
};

function setupHoverMenuOn(el) {
	// Use mouse event to control "hover" state, including delay for
	// users with mouse precision issues.
	let timer;
	el.addEventListener("mouseover", function(e) {
		toggleClass(el, "ba11--open", true);
		clearTimeout(timer);
	});
	el.addEventListener("mouseout", function(e) {
		el.blur();
		closeSubmenusExcept(el);
		toggleClass(el, "ba11--open", false);
	});
	el.addEventListener("touchstart", function(e) {
		// If this element is already open, don't prevent event
		// from bubbling
		if (
			hasClass(el, "ba11--open") ||
			(hasClass(el, "ba11-top-shared-section") &&
				window.innerWidth &&
				window.innerWidth <= 700)
		) {
			e.stopPropagation();
		} else {
			e.preventDefault();
		}
		toggleClass(el, "ba11--open", true);
		closeSubmenusExcept(el);
		el.focus();
	});
	el.addEventListener("touchcancel", function(e) {
		toggleClass(el, "ba11--open", false);
		e.preventDefault();
		el.blur();
	});
}

function toggleElOpen(
	containerEl,
	toggleEl,
	{override, openCallback = null, closeCallback = null} = {}
) {
	const close = () => {
		toggleClass(containerEl, "ba11--open", false);
		toggleEl && toggleEl.setAttribute("aria-expanded", "false");
		closeCallback && closeCallback(containerEl);
	};
	const open = () => {
		closeSubmenusExcept(containerEl);
		toggleClass(containerEl, "ba11--open", true);
		toggleEl && toggleEl.setAttribute("aria-expanded", "true");
		openCallback && openCallback(containerEl);
	};

	if (typeof override !== "undefined") {
		if (override) {
			open();
		} else {
			close();
		}
	} else if (hasClass(containerEl, "ba11--open")) {
		close();
	} else {
		open();
	}
}

const closeSubmenusExcept = el => {
	getItemsWithSubmenus().forEach(item => {
		if (!el || item !== el) {
			toggleClass(item, "ba11--open", false);
		}
	});
};

function toggleExpandableOpen(containerEl, override) {
	const toggleEl = containerEl.querySelector(".ba11-expandable-toggle-el");
	toggleElOpen(containerEl, toggleEl, {override});
}

function throttle(func, delay) {
	let firedRecently = false;
	function throttledFunc(...args) {
		if (!firedRecently) {
			func(...args);
			firedRecently = true;
			setTimeout(() => {
				firedRecently = false;
			}, delay);
		}
	}
	return throttledFunc;
}

function setupToggleOn(
	containerEl,
	toggleEl,
	{leaveAsLinkFunc = false, openCallback = null, closeCallback = null} = {}
) {
	// In some cases, we want to conditionally leave the anchor
	// within as a link
	const throttledToggleOnClick = throttle(e => {
		toggleElOpen(containerEl, toggleEl, {openCallback, closeCallback});
	}, 250);

	function toggleOnClick(e) {
		throttledToggleOnClick(e);
		e.preventDefault();
		e.stopPropagation();
		return false;
	}

	const listenerFunc = leaveAsLinkFunc
		? function(e) {
				if (!leaveAsLinkFunc(e)) toggleOnClick(e);
		  }
		: toggleOnClick;

	onClickOrEnter(toggleEl, listenerFunc);
}

const preventBadQtyInput = function(el, length) {
	// Limits keys to digits, navigation, deleting, and selection
	el.addEventListener("keydown", evt => {
		const validKeysAtMax = [
			"Backspace",
			"Delete",
			"Tab",
			"ArrowLeft",
			"ArrowRight",
			"ArrowUp",
			"ArrowDown",
			"Shift",
		];
		const validKeys = validKeysAtMax.concat([
			"0",
			"1",
			"2",
			"3",
			"4",
			"5",
			"6",
			"7",
			"8",
			"9",
		]);
		const atMaxLength =
			el.value && el.value.length && el.value.length === length;
		if (
			!validKeys.includes(evt.key) ||
			(atMaxLength &&
				!validKeysAtMax.includes(evt.key) &&
				//If text is selected, we want to allow the user to type in the default way, so we don't prevent default in that case.
				document.getSelection().toString().length === 0)
		) {
			evt.preventDefault();
		}
	});
};

//
// Marketing logs
//

const logMarketingEvent = function(type, eventRefID) {
	const clientDeviceID = getCookie("ba_clientDeviceID");
	if (!clientDeviceID) return;
	const payload = {
		events: [
			{
				clientDeviceID,
				timestamp: Date.now(),
				type,
				eventRefID,
			},
		],
	};
	runAjax({
		endpoint: "/api/analytics/logMarketingEvents",
		payload,
		timeout: AJAX_TIMEOUT,
	});
};

const logMarketingSource = function() {
	const search = window && window.location && window.location.search;
	const searchAsObject = queryString.parse(search);
	const utmParams = {
		utm_source: "source",
		utm_medium: "medium",
		utm_campaign: "campaign",
		utm_content: "content",
		utm_term: "term",
	};

	// Only log if one of the query string parameters is present
	const logSource = clientDeviceID => {
		// check if there are any properties that are the same from the utm params vs found params
		const foundParamNames = Object.keys(searchAsObject);
		const utmParamNames = Object.keys(utmParams);
		let foundMarketingParam = false;
		for (let i = 0; i < utmParamNames.length; i++) {
			if (foundParamNames.includes(utmParamNames[i])) {
				foundMarketingParam = true;
				break;
			}
		}

		if (foundMarketingParam) {
			const newParams = {};
			utmParamNames.forEach(paramName => {
				const friendlyName = utmParams[paramName];
				if (!friendlyName) return;
				let qsValue = searchAsObject[paramName] || "";
				if (Array.isArray(searchAsObject[paramName])) {
					qsValue = qsValue[0];
				}
				newParams[friendlyName] = String(qsValue).replace(/\W/g, "") || null;
			});
			runAjax({
				endpoint: "/api/analytics/logMarketingSource",
				payload: {
					msParams: newParams,
					clientDeviceID,
				},
			});
		}
	};

	const clientDeviceID = getCookie("ba_clientDeviceID");
	if (!clientDeviceID) {
		runAjax(
			{
				endpoint: "/api/analytics/getClientDeviceID",
				timeout: AJAX_TIMEOUT,
			},
			(err, res) => {
				if (!err && res && res.success && res.clientDeviceID) {
					setCookie("ba_clientDeviceID", res.clientDeviceID, COOKIE_DURATION);
					logSource(res.clientDeviceID);
				}
			}
		);
	} else {
		logSource(clientDeviceID);
	}
};

//
// Google Marketing Tools
//

function sendDataLayerEvent(eventName, data) {
	if (process.env.NODE_ENV !== "production") {
		// eslint-disable-next-line no-console
		console.info(
			`%cSending data layer event "${eventName}"%c with data ${JSON.stringify(
				data,
				null,
				2
			)}`,
			"color:black;background-color:lightblue;",
			"color:default;background-color:default;"
		);
	} else if (global.pushToDataLayer) {
		global.pushToDataLayer({event: eventName, eventModel: data});
	}
}

//
// Ajax
//

function runAjax(props, cb) {
	props = props || {};
	const endpoint = props.endpoint;
	const timeout = props.timeout;
	const payload = props.payload;

	let alreadyCalled = false;
	const realCB = function(err, res) {
		if (alreadyCalled) return;
		alreadyCalled = true;
		if (err || res.error) onAjaxErrorGlobal(props, err, res);
		cb(err, res);
	};

	const ajax = new XMLHttpRequest();
	ajax.addEventListener("load", function() {
		if (this.status !== 200) {
			realCB({status: this.status, message: this.statusText});
		}
		try {
			const response = JSON.parse(this.responseText) || {};
			realCB(null, response);
		} catch (ex) {
			realCB({message: ex.message});
		}
	});
	ajax.addEventListener("error", function() {
		realCB({status: this.status, message: this.statusText});
	});
	let sendArg;
	ajax.open("POST", endpoint);
	if (payload) {
		ajax.setRequestHeader("Content-Type", "application/json;charset=UTF-8");
		sendArg = JSON.stringify(payload);
	}
	ajax.send(sendArg);

	if (timeout) {
		setTimeout(() => realCB({message: "Timeout"}), timeout);
	}
}

function onAjaxErrorGlobal(props, err, res) {
	if (res && res.error && res.error === "E_NO_IMITATING") {
		const noImitatingErrorEl = document.body.querySelector(
			"#imitatorIndicator .ba11-imitator-unavailable-error"
		);
		if (noImitatingErrorEl) {
			const endpointName = props.endpoint || "endpoint";
			noImitatingErrorEl.querySelector(
				".imitate-error-endpoint"
			).innerText = endpointName;
			toggleClass(noImitatingErrorEl, "ba11--hidden", false);
			setTimeout(
				() => toggleClass(noImitatingErrorEl, "ba11--hidden", true),
				10000
			);
		}
	}
}

// ClassWallet setup helpers
const doOpenModal = (event, modal) => {
	document.body.classList.add("wf-modal--open");
	modal.style = "display: block;";
	event.preventDefault();
	event.stopPropagation();
};

const doCloseModal = (event, modal) => {
	document.body.classList.remove("wf-modal--open");
	modal.style = "display: none;";
	event.preventDefault();
	event.stopPropagation();
};
const showModal = (modalToShow, event) => {
	const modalCloseButton = $find(modalToShow, ".close-block")[0];
	const dismissButton = $find(modalToShow, "a.dismiss-btn")[0];
	if (modalToShow) {
		doOpenModal(event, modalToShow);

		modalCloseButton?.addEventListener("click", e =>
			doCloseModal(e, modalToShow)
		);
		dismissButton?.addEventListener("click", e => doCloseModal(e, modalToShow));
	}
};

function disableLiveClassesLinks(modalToShow) {
	// disable all links to /live-classes
	// (*=) searches for hrefs that contain the string
	// (=) searches for exact matches
	$each(
		document,
		'a[href="/live-classes"], a[href*="/program-comparison"]',
		el => {
			// only do this for the links that are NOT in the error modal
			if (!el.closest(".live-classes-error")) {
				el.addEventListener("click", evt => {
					// provide a message to the user that live-classes cannot be bought
					// with CW
					if (modalToShow) showModal(modalToShow, evt);
					evt.preventDefault();
				});
			}
		}
	);
}

module.exports = {
	AJAX_TIMEOUT,
	compileTemplate,
	clamp,
	getCookie,
	setCookie,
	clearCookie,
	hasPermKey,
	hasPermRole,
	$find,
	$each,
	removeEl,
	toggleClass,
	toggleAttribute,
	hasClass,
	isValidEmail,
	isShallowEqual,
	uniqBy,
	onClickOrEnter,
	onEnter,
	getRadioSelectedValue,
	getSelectionValue,
	getCheckboxSelectedValues,
	setInnerHtmlIfDiff,
	scrollToEl,
	isFocusable,
	getFocusableElements,
	getItemsWithSubmenus,
	setupHoverMenuOn,
	toggleElOpen,
	toggleExpandableOpen,
	throttle,
	setupToggleOn,
	preventBadQtyInput,
	logMarketingEvent,
	logMarketingSource,
	sendDataLayerEvent,
	runAjax,
	onAjaxErrorGlobal,
	closeSubmenusExcept,
	showModal,
	disableLiveClassesLinks,
};
